import {
	Alert,
	Button,
	Card,
	Divider,
	Group,
	SegmentedControl,
	SimpleGrid,
	Stack,
	Text,
	Title,
} from "@mantine/core";
import {
	IconAlertCircle,
	IconDeviceFloppy,
	IconInfoCircle,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { toAssistantLang } from "@/components/assistant/assistant.logic";
import { assistantAutoPointTexts } from "@/locales/assistant-auto-point";
import {
	assistantStyleTexts,
	fillStyleText,
	styleErrorText,
} from "@/locales/assistant-style";
import { assistantVoiceExperimentsTexts } from "@/locales/assistant-voice-experiments";
import { assistantWaitPhrasesTexts } from "@/locales/assistant-wait-phrases";
import { assistantWaitSoundTexts } from "@/locales/assistant-wait-sound";
import { i18nStore } from "@/store/i18n";
import {
	type AssistantStyleDto,
	isStylePreset,
	STYLE_PRESETS,
	type StylePreset,
} from "@/types/ai-assistant-style";
import { saveStyle } from "./ai-assistant.api";
import { StyleAutoPoint } from "./style-auto-point";
import { StyleCoreRules } from "./style-core-rules";
import { StylePreview } from "./style-preview";
import { StyleRulesTable } from "./style-rules-table";
import {
	normalizeStyleSettings,
	validateStyleSettings,
} from "./style-settings.logic";
import { StyleVoiceExperiments } from "./style-voice-experiments";
import { StyleWaitPhrases } from "./style-wait-phrases";
import { StyleWaitSound } from "./style-wait-sound";
import { StyleWordsEditor } from "./style-words-editor";

interface Props {
	style: AssistantStyleDto;
	/** Nama asisten dari pengaturan umum (judul & deskripsi). */
	assistantName: string;
	disabled: boolean;
	/** Endpoint /style belum ada di server → nilai default, form terkunci. */
	unavailable?: boolean;
	onSaved: (style: AssistantStyleDto) => void;
}

/** Bagian "Gaya & aturan Jenna" (#59) — disimpan lewat PUT /style, terpisah dari pengaturan umum. */
export function StyleSection({
	style,
	assistantName,
	disabled,
	unavailable = false,
	onSaved,
}: Props) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = toAssistantLang(uiLang);
	const t = assistantStyleTexts[lang];
	const [form, setForm] = useState(style);
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState<string | null>(null);
	const [saved, setSaved] = useState(false);

	useEffect(() => setForm(style), [style]);

	const normalized = useMemo(() => normalizeStyleSettings(form), [form]);
	const errors = useMemo(() => validateStyleSettings(normalized), [normalized]);
	const errorFor = (key: string) => {
		const code = errors[key];
		return code ? styleErrorText(t, code) : undefined;
	};
	const dirty = JSON.stringify(normalized) !== JSON.stringify(style);
	const set = <K extends keyof AssistantStyleDto>(
		key: K,
		value: AssistantStyleDto[K],
	) => {
		setSaved(false);
		setForm((prev) => ({ ...prev, [key]: value }));
	};

	const handleSave = async () => {
		setSaving(true);
		setSaveError(null);
		try {
			onSaved(await saveStyle(normalized));
			setSaved(true);
		} catch (err) {
			setSaveError(err instanceof Error ? err.message : String(err));
		} finally {
			setSaving(false);
		}
	};

	const presetPicker = (key: "chatStyle" | "voiceStyle", label: string) => (
		<Stack gap={4}>
			<Text size="sm" fw={500}>
				{label}
			</Text>
			<SegmentedControl
				value={form[key]}
				disabled={disabled}
				onChange={(v) => {
					if (isStylePreset(v)) set(key, v);
				}}
				data={STYLE_PRESETS.map((p: StylePreset) => ({
					value: p,
					label: t.styles.presets[p],
				}))}
				data-style-picker={key}
			/>
		</Stack>
	);

	return (
		<Card withBorder radius="md" p="lg" data-style-section>
			<Title order={4} mb={4}>
				{fillStyleText(t.title, { name: assistantName })}
			</Title>
			<Text size="sm" c="dimmed" mb="md">
				{t.description}
			</Text>
			<Stack gap="md">
				{unavailable && (
					<Alert color="blue" icon={<IconInfoCircle size={16} />}>
						{t.unavailable}
					</Alert>
				)}
				<StyleCoreRules t={t} lang={lang} />
				<SimpleGrid cols={{ base: 1, sm: 2 }}>
					{presetPicker("chatStyle", t.styles.chat)}
					{presetPicker("voiceStyle", t.styles.voice)}
				</SimpleGrid>
				<StyleWordsEditor
					t={t}
					words={form.expressiveWords}
					frequency={form.expressiveFrequency}
					error={errorFor("expressiveWords")}
					disabled={disabled}
					onWordsChange={(w) => set("expressiveWords", w)}
					onFrequencyChange={(f) => set("expressiveFrequency", f)}
				/>
				<Divider />
				<StyleWaitPhrases
					t={assistantWaitPhrasesTexts[lang]}
					phrases={form.voiceWaitPhrases}
					error={errorFor("voiceWaitPhrases")}
					disabled={disabled}
					onChange={(p) => set("voiceWaitPhrases", p)}
				/>
				<Divider />
				<StyleVoiceExperiments
					t={assistantVoiceExperimentsTexts[lang]}
					values={form}
					disabled={disabled}
					onChange={(key, value) => set(key, value)}
				/>
				<Divider />
				<StyleWaitSound
					t={assistantWaitSoundTexts[lang]}
					enabled={form.waitSoundEnabled}
					volume={form.waitSoundVolume}
					disabled={disabled}
					onEnabledChange={(v) => set("waitSoundEnabled", v)}
					onVolumeChange={(v) => set("waitSoundVolume", v)}
				/>
				<Divider />
				<StyleAutoPoint
					t={assistantAutoPointTexts[lang]}
					enabled={form.autoPointEnabled}
					disabled={disabled}
					onChange={(v) => set("autoPointEnabled", v)}
				/>
				<Divider />
				<StyleRulesTable
					t={t}
					name={assistantName}
					rules={form.extraRules}
					errorFor={errorFor}
					disabled={disabled}
					onChange={(r) => set("extraRules", r)}
				/>
				<Divider />
				<StylePreview
					t={t}
					lang={lang}
					chatStyle={form.chatStyle}
					voiceStyle={form.voiceStyle}
					words={normalized.expressiveWords}
				/>
				{saveError && (
					<Alert color="red" icon={<IconAlertCircle size={16} />}>
						{saveError}
					</Alert>
				)}
				<Group justify="flex-end">
					{saved && !dirty && (
						<Text size="sm" c="green">
							{t.saved}
						</Text>
					)}
					<Button
						leftSection={<IconDeviceFloppy size={16} />}
						onClick={handleSave}
						loading={saving}
						disabled={disabled || !dirty || Object.keys(errors).length > 0}
						data-style-save
					>
						{t.save}
					</Button>
				</Group>
			</Stack>
		</Card>
	);
}
